import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); refreshUser(); return; }
        if (data.status === "expired") { setState("expired"); return; }
      } catch { /* keep polling */ }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-6 py-28 text-center">
      {state === "checking" && (
        <div data-testid="order-checking">
          <Loader2 size={48} className="animate-spin text-[#E8956B] mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold">Confirming your payment...</h1>
          <p className="text-neutral-500 mt-2">Please wait a moment.</p>
        </div>
      )}
      {state === "success" && (
        <div data-testid="order-success">
          <CheckCircle2 size={56} className="text-[#E8956B] mx-auto mb-6" />
          <h1 className="font-display text-3xl font-bold">Payment Successful! 🎉</h1>
          <p className="text-neutral-500 mt-3">
            Your payment is held safely in escrow. The seller will ship your item, and you'll release the payment once you confirm delivery.
          </p>
          <button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")} className="mt-8 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-3 btn-glow transition-all">
            View My Purchases
          </button>
        </div>
      )}
      {(state === "error" || state === "expired" || state === "timeout") && (
        <div data-testid="order-error">
          <XCircle size={56} className="text-red-400 mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold">
            {state === "expired" ? "Payment session expired" : state === "timeout" ? "Still processing" : "Something went wrong"}
          </h1>
          <p className="text-neutral-500 mt-2">Please check your dashboard or try again.</p>
          <button onClick={() => navigate("/shop")} className="mt-6 border-2 border-[#E8956B] text-[#E8956B] font-bold rounded-full px-8 py-3 hover:bg-[#E8956B] hover:text-white transition-all">
            Back to Shop
          </button>
        </div>
      )}
    </div>
  );
}
